import {
  GenerateKentRebalancingPlanParams,
  KentRebalanceErrorStatus,
  KentWorkload,
  KentRebalancingPlan,
  RebalanceErrorStatus,
  RebalancingPlanOperation,
  Workload,
  GenerateRebalancingPlanCombinedParams,
} from '@cast/types';

const errorCodeMap: Partial<
  Record<KentRebalanceErrorStatus, RebalanceErrorStatus>
> = {
  [KentRebalanceErrorStatus.GENERATION_FAILED]:
    RebalanceErrorStatus.rebalancingPlanGenerationFailed,
  [KentRebalanceErrorStatus.UPSCALING_FAILED]:
    RebalanceErrorStatus.upscalingFailed,
  [KentRebalanceErrorStatus.NODE_DRAIN_FAILED]:
    RebalanceErrorStatus.nodeDrainFailed,
  [KentRebalanceErrorStatus.NODE_CREATE_FAILED]:
    RebalanceErrorStatus.nodeCreateFailed,
  [KentRebalanceErrorStatus.NODE_PREPARE_FAILED]:
    RebalanceErrorStatus.nodePrepareFailed,
  [KentRebalanceErrorStatus.NODE_DELETE_FAILED]:
    RebalanceErrorStatus.nodeDeleteFailed,
  [KentRebalanceErrorStatus.INSUFFICIENT_SAVINGS]:
    RebalanceErrorStatus.achievedSavingsBelowThreshold,
  [KentRebalanceErrorStatus.INSUFFICIENT_CAPACITY]:
    RebalanceErrorStatus.insufficientCapacity,
  [KentRebalanceErrorStatus.TIMEOUT]:
    RebalanceErrorStatus.rebalancingPlanTimeout,
  [KentRebalanceErrorStatus.REBALANCING_ERROR_CODE_UNSPECIFIED]:
    RebalanceErrorStatus.unknown,
  [KentRebalanceErrorStatus.UNKNOWN]: RebalanceErrorStatus.unknown,
};

export const mapKentErrorToOldError = (
  kentErrorStatus: KentRebalanceErrorStatus
): RebalanceErrorStatus => {
  return errorCodeMap[kentErrorStatus] || RebalanceErrorStatus.unknown;
};

const buildPlanOperationsForType = ({
  type,
  total,
  completed,
}: {
  type: RebalancingPlanOperation['type'];
  total: number;
  completed: number;
}): RebalancingPlanOperation[] => {
  return Array.from({ length: total }, (_, i) => ({
    type,
    // Kent plan doesn't provide per-operation finishedAt timestamps,
    // so we use the current date as a placeholder.
    // This is safe because downstream code only checks its presence (not value) to determine completion.
    finishedAt: i < completed ? new Date().toISOString() : undefined,
  }));
};

export const buildPlanOperationsFromKentProgress = (
  progress: KentRebalancingPlan['executionProgress']
): RebalancingPlanOperation[] => {
  if (!progress) {
    return [];
  }

  const createOps = buildPlanOperationsForType({
    type: 'create_node',
    total: progress.nodesToCreate ?? 0,
    completed: progress.nodesCreated ?? 0,
  });

  const drainOps = buildPlanOperationsForType({
    type: 'drain_node',
    total: progress.nodesToDrain ?? 0,
    completed: progress.nodesDrained ?? 0,
  });

  const deleteOps = buildPlanOperationsForType({
    type: 'delete_node',
    total: progress.nodesToDelete ?? 0,
    completed: progress.nodesDeleted ?? 0,
  });

  return [...createOps, ...drainOps, ...deleteOps];
};

export const mapCombinedCreateRebalancingPlanParamsToKentPlanParams = (
  params: GenerateRebalancingPlanCombinedParams,
  clusterId: string,
  organizationId: string
): GenerateKentRebalancingPlanParams => {
  return {
    clusterId,
    organizationId,
    rebalancingNodes: params.rebalancingNodeNames,
    aggressiveModeConfig: params.kentAggressiveModeConfig,
  };
};

export function convertKentWorkloadToRegularWorkload(
  kentWorkload: KentWorkload
): Workload {
  return {
    name: kentWorkload.name ?? '',
    resource: kentWorkload.resource ?? '',
    namespace: kentWorkload.namespace ?? '',
    costImpact: kentWorkload.costImpact,
    replicas: kentWorkload.replicas ?? 0,
    milliCpu: kentWorkload.milliCpu ?? 0,
    memoryMib: kentWorkload.memoryMib ?? 0,
    nodes: kentWorkload.nodes?.map((node) => ({
      id: node.name ?? '',
      name: node.name ?? '',
      specs: {
        milliCpu: node.specs?.milliCpu ?? 0,
        memoryMib: node.specs?.memoryMib ?? 0,
        instanceType: node.specs?.instanceType ?? '',
      },
      status: {
        migrationStatus: node.status?.migrationStatus,
        issues: node.status?.issues,
      },
      totalPods: node.totalPods ?? 0,
      totalProblematicPods: node.totalProblematicPods ?? 0,
      workloadReplicas: node.workloadReplicas ?? 0,
    })),
    status: kentWorkload.status,
    issues: (kentWorkload.issues || [])?.map((issue) => ({
      kind: issue.kind || '',
      description: issue.description ?? '',
    })),
  };
}
